/**
 * Groups Candid write-back rows by `use_case` and sorts each group
 * oldest-first by `created_at`.
 *
 * @module groupCandidWritebacksByUseCase
 */
import {
  CandidWritebackRecord,
  CandidWritebackUseCase,
} from "../types/candidWriteback";

/** Every write-back use case, in a stable key order. */
const CANDID_WRITEBACK_USE_CASES: CandidWritebackUseCase[] = [
  "authorization",
  "document",
  "note",
];

/**
 * Write-back rows keyed by use case. Missing groups are empty arrays.
 */
export type CandidWritebacksByUseCase = Record<
  CandidWritebackUseCase,
  CandidWritebackRecord[]
>;

/**
 * Builds an empty group map so callers can always read every use-case key.
 *
 * @returns Empty authorization, document, and note arrays
 */
const createEmptyWritebackGroups = (): CandidWritebacksByUseCase => ({
  authorization: [],
  document: [],
  note: [],
});

/**
 * Compares two ISO-8601 timestamps so earlier times sort first.
 *
 * @param left - First write-back row
 * @param right - Second write-back row
 * @returns Negative when `left` is older than `right`
 */
const compareCreatedAtAscending = (
  left: CandidWritebackRecord,
  right: CandidWritebackRecord,
): number => left.created_at.localeCompare(right.created_at);

/**
 * Groups write-back rows by `use_case` and sorts each group by `created_at`
 * ascending. Does not mutate `records`.
 *
 * @param records - Write-back rows from the history API
 * @returns Rows grouped by use case, each group oldest-first
 */
export const groupCandidWritebacksByUseCase = (
  records: CandidWritebackRecord[],
): CandidWritebacksByUseCase => {
  const grouped = createEmptyWritebackGroups();

  for (const record of records) {
    grouped[record.use_case].push(record);
  }

  for (const useCase of CANDID_WRITEBACK_USE_CASES) {
    grouped[useCase] = [...grouped[useCase]].sort(compareCreatedAtAscending);
  }

  return grouped;
};
